import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ArrowLeft, Check, AlertTriangle } from "lucide-react";
import DocumentEditor from "../components/DocumentEditor";
import { useAuth } from "../context/ApiKeyContext";
import PopUp from "../components/PopUp";
import FileDownload from "../components/FileDownload";
import FeedbackModal from "../components/FeedbackModal";
import { useTranslation } from "../hooks/useTranslation";
import { getApiBaseUrl } from "../config";
import {
  readServiceCustomization,
  CONTESTATION_SERVICE,
  DOCUMENT_CREATION_PRODUCT_CODES,
} from "../utils/serviceCustomizationApi";

/** Indica se o item da API tem referências (flag ou listas/metadados), alinhado ao DocumentEditor. */
function inferPossuiReferenciasFromApiItem(item) {
  if (!item) return false;
  if (item.possui_referencias === true || item.possui_referencias === 1) {
    return true;
  }
  const rd = item.referencias_dados;
  const re = item.referencias_escrita;
  const ru = item.referencias_usuario;
  if (Array.isArray(rd) && rd.length > 0) return true;
  if (Array.isArray(re) && re.length > 0) return true;
  if (Array.isArray(ru) && ru.length > 0) return true;
  if (item.tipo_item === "IMAGEM") return true;
  const m = item.metadados || {};
  if (m.token != null && String(m.token).length > 0) {
    if (m.pagina != null || m.coordenadas != null) return true;
  }
  if (m.id_documento != null && m.pagina != null) return true;
  return false;
}

/**
 * Converte os dados da API para o formato esperado pelo DocumentEditor
 */
const convertApiDataToEditorFormat = (apiData) => {
  if (
    !apiData ||
    !apiData.documentos_criados ||
    apiData.documentos_criados.length === 0
  ) {
    return null;
  }

  // Converter cada documento
  const documents = apiData.documentos_criados.map((doc) => ({
    id: doc.id_documento,
    title: doc.descricao,
    tipo: doc.tipo,
    editavel: doc.editavel,
    items: (doc.itens || []).map((item) => {
      // Mapear tipo_item da API para o formato do editor
      let type = "text";
      let content = "";
      let imageUrl = null;
      let caption = null;

      if (item.tipo_item === "TEXTO") {
        type = "text";
        content = item.metadados?.texto || "";
      } else if (item.tipo_item === "IMAGEM") {
        type = "image";
        // URL da imagem será carregada posteriormente via API
        imageUrl = null; // TODO: Implementar chamada para carregar imagem
        caption = item.metadados?.texto_de_interesse || "";
      }

      return {
        id: item.id,
        type: type,
        content: content,
        imageUrl: imageUrl,
        caption: caption,
        reviewed: item.aprovado_por !== null && item.data_aprovacao !== null,
        possui_referencias: inferPossuiReferenciasFromApiItem(item),
        // Armazenar metadados originais para referência futura
        metadata: item.metadados,
        aprovado_por: item.aprovado_por,
        data_aprovacao: item.data_aprovacao,
        // Indicador de item criado por IA
        indicador_ia: item.indicador_ia,
        // Hierarquia do item (para exclusão de seção) - campo opcional
        hierarquia: item.hierarquia || null,
      };
    }),
  }));

  const firstResultFile =
    Array.isArray(apiData.result_files) && apiData.result_files.length > 0
      ? apiData.result_files.find((f) => f && f.token) || apiData.result_files[0]
      : null;

  return {
    job_id: apiData.job_id,
    status: apiData.status,
    documents: documents,
    // Capturar dados do documento final para download (quando status é DONE)
    result_docx: apiData.result_docx || firstResultFile || null,
  };
};

/**
 * Converte documentos do formato do editor para o formato da API (documentos_criados).
 * Usado ao aprovar para enviar o documento revisado à task FINAL_DOC_CREATION.
 */
const convertEditorDocumentsToApiFormat = (documents) => {
  if (!documents || documents.length === 0) return [];
  return documents.map((doc) => ({
    id_documento: doc.id,
    descricao: doc.title,
    tipo: doc.tipo,
    editavel: doc.editavel,
    itens: (doc.items || []).map((item) => {
      if (item.type === "text") {
        return {
          id: item.id,
          tipo_item: "TEXTO",
          metadados: { texto: item.content || "" },
        };
      }
      return {
        id: item.id,
        tipo_item: "IMAGEM",
        metadados: {
          token: item.metadata?.token ?? "",
          texto_de_interesse: item.caption ?? "",
        },
      };
    }),
  }));
};

/**
 * Página de revisão de documentos jurídicos
 * Genérica para ser usada com qualquer tipo de documento
 */
const DocumentReviewPage = () => {
  const history = useHistory();
  const { documentId, documentType } = useParams();
  const { token, cell, hasRole } = useAuth();
  const API_BASE_URL = getApiBaseUrl();
  const { t } = useTranslation("common");

  const [documents, setDocuments] = useState([]); // Lista de documentos disponíveis
  const [selectedDocumentId, setSelectedDocumentId] = useState(null); // ID do documento selecionado
  const [currentDocument, setCurrentDocument] = useState(null); // Documento atualmente sendo visualizado
  const [allDocuments, setAllDocuments] = useState([]); // Documentos fonte (para o sidebar)
  const [originDocuments, setOriginDocuments] = useState([]); // Documentos de origem disponíveis para visualização
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalItems: 0,
    reviewedItems: 0,
    reviewProgress: 0,
  });
  const [isApproving, setIsApproving] = useState(false); // Controla se está enviando aprovação
  const [showErrorPopup, setShowErrorPopup] = useState(false); // Controla popup de erro
  const [errorMessage, setErrorMessage] = useState(""); // Mensagem de erro
  const [jobStatus, setJobStatus] = useState(null); // Status do job (PENDING_APPROVAL, DONE, etc.)
  const [finalDocumentData, setFinalDocumentData] = useState(null); // Dados do documento final para download
  const [showIncompleteItemsPopup, setShowIncompleteItemsPopup] =
    useState(false); // Controla popup de itens incompletos
  const [incompleteItems, setIncompleteItems] = useState([]); // Lista de itens incompletos
  const [showUnreviewedItemsPopup, setShowUnreviewedItemsPopup] =
    useState(false); // Controla popup de itens não revisados
  const [unreviewedItems, setUnreviewedItems] = useState([]); // Lista de itens não revisados
  const [showUnreviewedItemsWarning, setShowUnreviewedItemsWarning] =
    useState(true); // Controla se mostra o aviso no popup de itens não revisados
  const [itemToSelect, setItemToSelect] = useState(null); // ID do item a ser selecionado no editor
  const [reviewRequired, setReviewRequired] = useState(true); // Configuração de revisão obrigatória (default: true)
  const [showConfirmFinishPopup, setShowConfirmFinishPopup] = useState(false); // Controla popup de confirmação de finalização
  const [showFeedbackModal, setShowFeedbackModal] = useState(false); // Controla modal de feedback

  // Estados para controlar o carregamento das APIs
  const [loadingSteps, setLoadingSteps] = useState({
    document: false,
    sources: false,
    metadata: false,
  });

  // Função para recarregar documentos de origem
  const reloadOriginDocuments = async () => {
    if (!documentId) return null;

    try {
      console.log("🔄 Recarregando documentos de origem...");
      const originUrl = `${API_BASE_URL}/doc_creator/review/origin-documents/${documentId}`;
      const originResponse = await fetch(originUrl, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (originResponse.ok) {
        const originData = await originResponse.json();
        console.log("✅ Documentos de origem recarregados:", originData);

        if (originData.documentos_origem) {
          setOriginDocuments(originData.documentos_origem);
          return originData.documentos_origem;
        }
      } else {
        console.warn(
          "Não foi possível recarregar documentos de origem:",
          originResponse.status
        );
      }
      return null;
    } catch (err) {
      console.warn("Erro ao recarregar documentos de origem:", err);
      return null;
    }
  };

  useEffect(() => {
    // Função para carregar os dados do documento via API
    const loadDocumentData = async () => {
      if (!documentId) {
        setError("ID do documento não fornecido");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log(`Carregando documento para revisão...`);
        console.log(`Job ID: ${documentId}`);
        console.log(`Tipo de documento: ${documentType}`);

        // Atualiza o estado de loading por etapa
        setLoadingSteps({ document: true, sources: false, metadata: false });

        // ETAPA 1: Carregar dados do documento
        const url = `${API_BASE_URL}/doc_creator/review/documents/${documentId}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `Erro na requisição: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();

        // Converter dados da API para formato do editor
        const convertedData = convertApiDataToEditorFormat(data);

        if (
          !convertedData ||
          !convertedData.documents ||
          convertedData.documents.length === 0
        ) {
          throw new Error("Nenhum documento encontrado na resposta da API");
        }

        setDocuments(convertedData.documents);

        // Armazenar o status do job
        setJobStatus(convertedData.status);

        // Selecionar o documento PRINCIPAL por padrão
        const principalDoc = convertedData.documents.find(
          (doc) => doc.tipo === "PRINCIPAL"
        );
        const defaultDoc = principalDoc || convertedData.documents[0];
        setSelectedDocumentId(defaultDoc.id);
        setCurrentDocument(defaultDoc);

        setLoadingSteps({ document: true, sources: true, metadata: false });

        // Se o status for DONE e houver result_docx na resposta, usar diretamente
        if (convertedData.status === "DONE" && convertedData.result_docx) {
          setFinalDocumentData(convertedData.result_docx);
        }

        // ETAPA 2: Carregar documentos de origem disponíveis para visualização
        try {
          const originUrl = `${API_BASE_URL}/doc_creator/review/origin-documents/${documentId}`;
          const originResponse = await fetch(originUrl, {
            method: "GET",
            headers: {
              accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          if (originResponse.ok) {
            const originData = await originResponse.json();

            // Armazenar os documentos de origem
            if (originData.documentos_origem) {
              setOriginDocuments(originData.documentos_origem);
            }
          }
        } catch (err) {
          // Não bloquear o carregamento se falhar
        }

        // Por enquanto, mantendo lista vazia para os documentos fonte (sources das referências)
        setAllDocuments([]);

        setLoadingSteps({ document: true, sources: true, metadata: true });

        // ETAPA 3: Carregar configuração de revisão obrigatória (cell_id do job)
        let jobCellId = data.cell_id ?? null;

        if (!jobCellId) {
          try {
            const jobsParams = new URLSearchParams({
              job_id: String(parseInt(documentId, 10)),
              code: "DOCUMENT_CREATION",
            });
            const jobsUrl = `${API_BASE_URL}/job/get_jobs_list?${jobsParams}`;
            const jobsResponse = await fetch(jobsUrl, {
              method: "GET",
              headers: {
                accept: "application/json",
                Authorization: `Bearer ${token}`,
                ServiceName: "DOCUMENT_CREATION",
              },
            });
            if (jobsResponse.ok) {
              const jobsData = await jobsResponse.json();
              if (jobsData.jobs && jobsData.jobs.length > 0) {
                const job = jobsData.jobs[0];
                jobCellId = job.cell_id ?? job.input_payload?.cell_id ?? null;
              }
            }
          } catch (err) {
            // Silenciosamente ignorar erro
          }
        }

        // Carregar configuração de revisão obrigatória
        if (jobCellId) {
          try {
            // Converter cell_id para número se necessário
            const cellIdNum =
              typeof jobCellId === "string" ? parseInt(jobCellId) : jobCellId;
            const reviewRequiredValue = await readServiceCustomization(
              token,
              cellIdNum,
              CONTESTATION_SERVICE.ATTRIBUTES.REVIEW_REQUIRED,
              DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
            );
            // Se não encontrar, usar default true
            const finalValue =
              reviewRequiredValue !== null && reviewRequiredValue !== undefined
                ? reviewRequiredValue
                : true;
            setReviewRequired(finalValue);
          } catch (err) {
            // Em caso de erro, usar default true
            setReviewRequired(true);
          }
        } else {
          // Se não tiver cell_id, usar default true
          setReviewRequired(true);
        }

        setLoading(false);
      } catch (err) {
        console.error("Erro ao carregar documento:", err);
        setError(err.message || "Erro ao carregar documento");
        setLoading(false);
      }
    };

    loadDocumentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, documentType, API_BASE_URL, token]);

  // Atualizar documento quando a seleção mudar
  useEffect(() => {
    if (selectedDocumentId && documents.length > 0) {
      const doc = documents.find((d) => d.id === selectedDocumentId);
      if (doc) {
        setCurrentDocument(doc);
      }
    }
  }, [selectedDocumentId, documents]);

  const handleDocumentChange = (updatedDocument) => {
    // Atualizar o documento atual
    setCurrentDocument(updatedDocument);

    // Atualizar na lista de documentos
    setDocuments((prevDocs) =>
      prevDocs.map((doc) =>
        doc.id === updatedDocument.id ? updatedDocument : doc
      )
    );

    // Aqui você pode implementar auto-save ou debounced save
    // TODO: Implementar auto-save das alterações
  };

  const handleStatsChange = (newStats) => {
    setStats(newStats);
  };

  const handleDocumentSelect = (documentId) => {
    setSelectedDocumentId(documentId);
  };

  /**
   * Abre o popup de itens não revisados sem o aviso
   * Usado quando o usuário clica no contador de pendentes
   */
  const handleOpenUnreviewedItemsPopup = () => {
    const unreviewed = validateUnreviewedItems();
    if (unreviewed.length > 0) {
      setUnreviewedItems(unreviewed);
      setShowUnreviewedItemsWarning(false); // Não mostrar aviso
      setShowUnreviewedItemsPopup(true);
    }
  };

  /**
   * Navega até um item específico no documento
   * Seleciona o documento correto se necessário e faz scroll até o item
   */
  const handleNavigateToItem = (item) => {
    // Fechar o popup
    setShowUnreviewedItemsPopup(false);

    // Selecionar o documento correto se o item estiver em outro documento
    if (item.documentId !== selectedDocumentId) {
      setSelectedDocumentId(item.documentId);
      // Aguardar o documento ser carregado antes de selecionar o item
      setTimeout(() => {
        setItemToSelect(item.itemId);
      }, 100);
    } else {
      // Selecionar o item diretamente
      setItemToSelect(item.itemId);
    }

    // Limpar a seleção após um tempo para permitir nova seleção
    setTimeout(() => {
      setItemToSelect(null);
    }, 1000);
  };

  /**
   * Valida se existem itens incompletos em todos os documentos editáveis
   * Retorna uma lista de itens incompletos com informações para exibição
   */
  const validateIncompleteItems = () => {
    const incomplete = [];

    // Verificar todos os documentos editáveis
    documents.forEach((doc) => {
      if (!doc.editavel) return; // Ignorar documentos não editáveis

      (doc.items || []).forEach((item, index) => {
        // Verificar itens de texto vazios
        if (item.type === "text") {
          const content = item.content?.trim() || "";
          if (!content) {
            incomplete.push({
              documentTitle: doc.title,
              documentId: doc.id,
              itemId: item.id,
              itemIndex: index + 1,
              type: "text",
              issue: "Texto vazio",
            });
          }
        }

        // Verificar itens de imagem sem imagem associada
        if (item.type === "image") {
          const hasImage = item.imageUrl || item.metadata?.token;
          if (!hasImage) {
            incomplete.push({
              documentTitle: doc.title,
              documentId: doc.id,
              itemId: item.id,
              itemIndex: index + 1,
              type: "image",
              issue: "Imagem não selecionada",
            });
          }
        }
      });
    });

    return incomplete;
  };

  /**
   * Valida se todos os itens criados por IA foram revisados em todos os documentos editáveis
   * Retorna uma lista de itens não revisados com informações para exibição
   * IMPORTANTE: Apenas itens criados por IA (com indicador_ia) são validados
   */
  const validateUnreviewedItems = () => {
    const unreviewed = [];

    // Verificar todos os documentos editáveis
    documents.forEach((doc) => {
      if (!doc.editavel) return; // Ignorar documentos não editáveis

      (doc.items || []).forEach((item, index) => {
        // Verificar se o item foi criado por IA (tem indicador_ia)
        const isAICreated =
          item.indicador_ia &&
          (item.indicador_ia === 1 || item.indicador_ia === 2);

        // Apenas validar itens criados por IA que não foram revisados
        if (isAICreated && !item.reviewed) {
          unreviewed.push({
            documentTitle: doc.title,
            documentId: doc.id,
            itemId: item.id,
            itemIndex: index + 1,
            type: item.type || "text",
            isAICreated: true,
            possui_referencias: item.possui_referencias || false,
            issue: "Item criado por IA - não revisado",
          });
        }
      });
    });

    return unreviewed;
  };

  const handleFinishDocument = async () => {
    // 0. Verificar se existem itens incompletos em documentos editáveis
    const incomplete = validateIncompleteItems();

    if (incomplete.length > 0) {
      setIncompleteItems(incomplete);
      setShowIncompleteItemsPopup(true);
      return; // Bloquear finalização até resolver os itens incompletos
    }

    // 1. Verificar se todos os itens criados por IA foram revisados em todos os documentos editáveis
    // (apenas se revisão for obrigatória)
    if (reviewRequired) {
      const unreviewed = validateUnreviewedItems();

      if (unreviewed.length > 0) {
        setUnreviewedItems(unreviewed);
        setShowUnreviewedItemsWarning(true); // Mostrar aviso quando abrir pela validação
        setShowUnreviewedItemsPopup(true);
        return; // Bloquear finalização até revisar todos os itens criados por IA
      }
    }

    // 2. Se tudo estiver ok, mostrar popup de confirmação
    setShowConfirmFinishPopup(true);
  };

  const handleConfirmFinish = async () => {
    // Fechar popup de confirmação
    setShowConfirmFinishPopup(false);

    // Fazer chamada à API para aprovar (com documento revisado para a geração do doc final)
    setIsApproving(true);

    try {
      const url = `${API_BASE_URL}/doc_creator/review/approve/${documentId}`;
      const documentos_criados = convertEditorDocumentsToApiFormat(documents);
      const body = {
        output_params: {
          approved: true,
          documentos_criados,
        },
      };
      console.log("🔄 Enviando aprovação com documento revisado:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Tratar erros específicos
        let errorMsg = "Erro ao aprovar documento";

        if (response.status === 403) {
          errorMsg =
            "Você não tem permissão para aprovar este documento ou o documento não está em estado de aprovação pendente.";
        } else if (response.status === 404) {
          errorMsg = "Documento não encontrado.";
        } else if (response.status === 500) {
          errorMsg = "Erro interno do servidor. Tente novamente mais tarde.";
        } else {
          const errorData = await response.json().catch(() => null);
          errorMsg =
            errorData?.message ||
            `Erro ${response.status}: ${response.statusText}`;
        }

        throw new Error(errorMsg);
      }

      const data = await response.json();
      console.log("✅ Aprovação bem-sucedida:", data);

      // 3. Abrir modal de feedback
      setShowFeedbackModal(true);
    } catch (error) {
      console.error("❌ Erro ao aprovar documento:", error);

      // Mostrar popup de erro
      setErrorMessage(
        error.message || "Erro desconhecido ao aprovar documento"
      );
      setShowErrorPopup(true);
    } finally {
      setIsApproving(false);
    }
  };

  const handleBack = () => {
    // Voltar para a lista de documentos baseado no tipo
    if (documentType === "contestacao") {
      history.push("/contestacoes");
    } else {
      history.push("/dashboard");
    }
  };

  const isReviewer = hasRole("DOCUMENT_CREATION", "reviewer");
  // Job concluído / encerrado na revisão: só leitura para todos
  const isDocumentFinalized =
    jobStatus === "DONE" ||
    jobStatus === "APPROVED" ||
    jobStatus === "FINALIZING";
  // Pendente de aprovação sem papel de revisor: só leitura (ex.: acesso direto por URL)
  const isReadOnly =
    isDocumentFinalized ||
    currentDocument?.editavel === false ||
    (jobStatus === "PENDING_APPROVAL" && !isReviewer);

  const hasPendingItems =
    (stats.totalItems || 0) - (stats.reviewedItems || 0) > 0;

  if (loading) {
    /* Lista das etapas em ordem de execução. Mantemos esta declaração
       próxima do render para facilitar a manutenção: adicionar/remover
       um passo só requer mexer aqui e em `loadingSteps`. */
    const steps = [
      { key: "document", labelKey: "review.loadingDocumentData" },
      { key: "sources", labelKey: "review.loadingSourceDocuments" },
      { key: "metadata", labelKey: "review.loadingMetadata" },
    ];
    /* Etapa "ativa" = primeira pendente. Tudo antes dela já está concluído;
       tudo depois é "futuro". Quando todas estão concluídas, `activeIdx`
       fica em `steps.length` (nenhum item ativo) — situação transitória
       até `loading` virar false e a UI sair desta tela. */
    const activeIdx = steps.findIndex((s) => !loadingSteps[s.key]);
    const doneCount = steps.filter((s) => loadingSteps[s.key]).length;
    /* Progresso visual: arrancamos com um pouco preenchido (10%) mesmo
       sem nenhuma etapa concluída, para o usuário ver imediatamente
       que algo está acontecendo. A cada etapa concluída, avança em
       passos iguais até 100%. */
    const progressPct = Math.round(
      10 + (doneCount / steps.length) * 90,
    );

    return (
      <div className="review-loading-page">
        <div className="review-loading-card">
          <div className="review-loading-spinner-lg" />
          <p className="review-loading-title">
            {t("review.loadingDocument")}
          </p>
          <p className="review-loading-subtitle">
            {t("review.loadingSubtitle")}
          </p>

          <div className="review-loading-progress">
            <div
              className="review-loading-progress-bar"
              role="progressbar"
              aria-valuenow={progressPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="review-loading-progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="review-loading-progress-label">
              {t("review.loadingProgressOf", {
                done: doneCount,
                total: steps.length,
              })}
            </span>
          </div>

          <div className="review-loading-steps">
            {steps.map((s, idx) => {
              const isDone = Boolean(loadingSteps[s.key]);
              const isActive = !isDone && idx === activeIdx;
              const stateClass = isDone
                ? "review-loading-step--done"
                : isActive
                  ? "review-loading-step--active"
                  : "review-loading-step--pending";
              return (
                <div key={s.key} className={`review-loading-step ${stateClass}`}>
                  <span className="review-loading-step-icon">
                    {isDone ? (
                      "✓"
                    ) : isActive ? (
                      <span className="review-loading-step-spinner" />
                    ) : (
                      "○"
                    )}
                  </span>
                  <span className="review-loading-step-text">
                    {t(s.labelKey)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Tela de erro
  if (error) {
    return (
      <div className="review-error-page">
        <div className="review-error-card">
          <div className="review-error-icon">⚠️</div>
          <h2 className="review-error-title">
            {t("review.errorLoadingDocument")}
          </h2>
          <p className="review-error-message">{error}</p>
          <button
            onClick={handleBack}
            className="ikaro-btn ikaro-btn-primary ikaro-btn-lg"
          >
            <ArrowLeft size={18} />
            Voltar
          </button>
        </div>
      </div>
    );
  }

  if (!currentDocument) {
    return (
      <div className="review-error-page">
        <div className="review-error-card">
          <h2 className="review-not-found-title">
            {t("review.documentNotFound")}
          </h2>
          <p className="review-not-found-message">
            {t("review.documentNotFoundMessage")}
          </p>
          <button
            onClick={handleBack}
            className="ikaro-btn ikaro-btn-primary ikaro-btn-lg"
          >
            <ArrowLeft size={18} />
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="review-page-root">
      {/* Header fixo compacto com progresso */}
      <div className="review-page-header">
        <div className="flex-row gap-16">
          <button onClick={handleBack} className="review-back-btn">
            <ArrowLeft size={16} />
            Voltar
          </button>

          {/* Barra de progresso - centralizada e expandida (escondida quando somente leitura) */}
          {!isReadOnly && (
            <div className="review-progress-wrapper">
              <div className="review-progress-label-row">
                <span>{t("review.reviewProgress")}</span>
                <span className="font-medium text-dark">
                  {stats.reviewProgress}%
                </span>
              </div>
              <div className="review-progress-track">
                <div
                  className="review-progress-fill"
                  style={{ width: `${stats.reviewProgress}%` }}
                />
              </div>
              {/* Contador de itens revisados e pendentes */}
              <div className="review-progress-stats">
                <span className="flex-row gap-4">
                  <span className="review-stat-value-success">
                    {stats.reviewedItems || 0}
                  </span>
                  <span>{t("review.reviewed")}</span>
                </span>
                <span className="review-stat-dot">•</span>
                <span
                  onClick={handleOpenUnreviewedItemsPopup}
                  className={`review-pending-link ${
                    hasPendingItems
                      ? "review-pending-link--active"
                      : "review-pending-link--inactive"
                  }`}
                >
                  <span className="review-stat-value-warning">
                    {(stats.totalItems || 0) - (stats.reviewedItems || 0)}
                  </span>
                  <span>{t("review.pending")}</span>
                </span>
              </div>
            </div>
          )}

          {/* Espaçador quando documento está em somente leitura */}
          {isReadOnly && <div className="flex-1" />}

          {/* Botão de Finalizar ou Baixar Documento */}
          {isDocumentFinalized ? (
            // Componente de Download (quando status é DONE)
            finalDocumentData ? (
              <div className="review-download-wrapper">
                <FileDownload file={finalDocumentData} />
              </div>
            ) : (
              <span className="review-not-available">
                {t("review.documentNotAvailable")}
              </span>
            )
          ) : isReadOnly ? (
            // Indicador de somente leitura (quando documento não é editável mas não está DONE)
            <span className="review-readonly-badge">
              {t("review.readOnlyDocument")}
            </span>
          ) : (
            // Botão de Finalizar Documento (quando em revisão)
            <button
              onClick={handleFinishDocument}
              disabled={isApproving}
              className="ikaro-btn ikaro-btn-primary ikaro-btn-sm flex-shrink-0"
            >
              {isApproving ? (
                <>
                  <div className="review-btn-spinner" />
                  {t("review.approving")}
                </>
              ) : (
                <>
                  <Check size={16} />
                  {t("review.finalizeDocument")}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Editor de documentos */}
      <div className="review-editor-wrapper">
        <DocumentEditor
          document={currentDocument}
          onDocumentChange={handleDocumentChange}
          onFinishDocument={handleFinishDocument}
          onStatsChange={handleStatsChange}
          allDocuments={allDocuments}
          originDocuments={originDocuments}
          documents={documents}
          selectedDocumentId={selectedDocumentId}
          onDocumentSelect={handleDocumentSelect}
          showHeader={false}
          editavel={!isReadOnly}
          jobId={documentId}
          token={token}
          apiBaseUrl={API_BASE_URL}
          onOriginDocumentsUpdated={reloadOriginDocuments}
          selectItemId={itemToSelect}
          cellId={cell?.value}
        />
      </div>

      {/* Popup de erro */}
      <PopUp
        open={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        title={t("review.approveErrorTitle")}
      >
        <div className="text-base text-dark mb-16">{errorMessage}</div>
        <button
          onClick={() => setShowErrorPopup(false)}
          className="ikaro-btn ikaro-btn-primary ikaro-btn-sm w-full justify-center"
        >
          {t("review.close")}
        </button>
      </PopUp>

      {/* Popup de itens incompletos */}
      <PopUp
        open={showIncompleteItemsPopup}
        onClose={() => setShowIncompleteItemsPopup(false)}
        title={t("review.incompleteItemsTitle")}
      >
        <div className="review-popup-warning">
          <AlertTriangle
            size={24}
            className="review-popup-warning-icon"
          />
          <div className="review-popup-warning-text">
            <strong>{t("review.attention")}</strong>{" "}
            {t("review.incompleteItemsFound")}
          </div>
        </div>

        <div className="review-popup-body-text">
          {t("review.toProceedComplete")}
          <ul className="review-popup-instructions">
            <li>{t("review.editItemsToAdd")}</li>
            <li>{t("review.deleteUnusedItems")}</li>
          </ul>
        </div>

        <div className="review-popup-count">
          {t("review.total")} <strong>{incompleteItems.length}</strong>{" "}
          {incompleteItems.length === 1
            ? t("review.incompleteItem")
            : t("review.incompleteItems")}
        </div>

        {/* Lista de itens incompletos */}
        <div className="review-popup-list">
          {incompleteItems.map((item) => (
            <div
              key={`${item.documentId}-${item.itemId}`}
              className="review-popup-list-item"
            >
              <div
                className={`review-popup-list-icon ${
                  item.type === "text"
                    ? "review-popup-icon-text"
                    : "review-popup-icon-image"
                }`}
              >
                {item.type === "text" ? "📝" : "🖼️"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="review-popup-item-title">
                  {item.documentTitle}
                </div>
                <div className="review-popup-item-subtitle">
                  {t("review.item")}
                  {item.itemIndex} • {item.issue}
                </div>
              </div>
              <div
                className={`review-popup-item-badge ${
                  item.type === "text"
                    ? "review-popup-badge-text"
                    : "review-popup-badge-image"
                }`}
              >
                {item.type === "text" ? t("review.text") : t("review.image")}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => setShowIncompleteItemsPopup(false)}
          className="ikaro-btn ikaro-btn-primary ikaro-btn-sm w-full justify-center"
        >
          {t("review.understoodReview")}
        </button>
      </PopUp>

      {/* Popup de itens não revisados */}
      <PopUp
        open={showUnreviewedItemsPopup}
        onClose={() => {
          setShowUnreviewedItemsPopup(false);
          setShowUnreviewedItemsWarning(true); // Resetar para mostrar aviso na próxima vez
        }}
        title={t("review.unreviewedItemsTitle")}
      >
        {/* Aviso - apenas quando showUnreviewedItemsWarning for true */}
        {showUnreviewedItemsWarning && (
          <>
            <div className="review-popup-warning">
              <AlertTriangle
                size={24}
                className="review-popup-warning-icon"
              />
              <div className="review-popup-warning-text">
                <strong>{t("review.attention")}</strong>{" "}
                {t("review.allItemsMustBeReviewed")}
              </div>
            </div>

            <div className="review-popup-body-text">
              {t("review.toProceedReviewAll")}
              <ul className="review-popup-instructions">
                <li>{t("review.clickItemToNavigate")}</li>
                <li>{t("review.clickCheckToMark")}</li>
              </ul>
            </div>
          </>
        )}

        <div className="review-popup-count">
          <strong>{unreviewedItems.length}</strong>{" "}
          {unreviewedItems.length === 1
            ? t("review.unreviewedItem")
            : t("review.unreviewedItems")}
          {unreviewedItems.length > 0 && (
            <span>
              {" "}
              •{" "}
              <strong>
                {
                  unreviewedItems.filter((item) => item.possui_referencias)
                    .length
                }
              </strong>{" "}
              {t("review.withReferences")}
              {" • "}
              <strong>
                {
                  unreviewedItems.filter((item) => !item.possui_referencias)
                    .length
                }
              </strong>{" "}
              {t("review.withoutReferences")}
            </span>
          )}
        </div>

        {/* Lista de itens não revisados */}
        <div className="review-popup-list">
          {unreviewedItems.map((item) => (
            <div
              key={`${item.documentId}-${item.itemId}`}
              onClick={() => handleNavigateToItem(item)}
              className="review-popup-list-item review-popup-list-item--clickable"
            >
              <div
                className={`review-popup-list-icon ${
                  item.isAICreated
                    ? "review-popup-icon-ai"
                    : item.type === "text"
                    ? "review-popup-icon-text"
                    : "review-popup-icon-image"
                }`}
              >
                {item.isAICreated ? "✨" : item.type === "text" ? "📝" : "🖼️"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="review-popup-item-title">
                  {item.documentTitle}
                </div>
                <div className="review-popup-item-subtitle">
                  {t("review.item")}
                  {item.itemIndex} • {item.issue}
                </div>
              </div>
              {item.isAICreated && (
                <div className="review-ai-badge">IA</div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={() => {
            setShowUnreviewedItemsPopup(false);
            setShowUnreviewedItemsWarning(true); // Resetar para mostrar aviso na próxima vez
          }}
          className="ikaro-btn ikaro-btn-primary ikaro-btn-sm w-full justify-center"
        >
          {showUnreviewedItemsWarning
            ? t("review.understoodReview")
            : t("review.close")}
        </button>
      </PopUp>

      {/* Popup de confirmação de finalização */}
      <PopUp
        open={showConfirmFinishPopup}
        onClose={() => setShowConfirmFinishPopup(false)}
        title={t("review.confirmFinishTitle")}
      >
        <div className="review-confirm-text">
          {t("review.confirmFinishMessage")}
        </div>

        <div className="review-confirm-actions">
          <button
            onClick={() => setShowConfirmFinishPopup(false)}
            className="ikaro-btn ikaro-btn-secondary ikaro-btn-md"
          >
            {t("review.cancel")}
          </button>
          <button
            onClick={handleConfirmFinish}
            disabled={isApproving}
            className="ikaro-btn ikaro-btn-primary ikaro-btn-md"
          >
            {isApproving ? (
              <>
                <div className="review-btn-spinner" />
                {t("review.approving")}
              </>
            ) : (
              <>
                <Check size={16} />
                {t("review.confirmFinish")}
              </>
            )}
          </button>
        </div>
      </PopUp>

      {/* Modal de Feedback */}
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          // Redirecionar após fechar o modal
          handleBack();
        }}
        jobId={documentId}
        token={token}
        apiBaseUrl={API_BASE_URL}
        onFeedbackSubmitted={(data) => {
          console.log("Feedback enviado:", data);
          // O modal será fechado automaticamente após sucesso
        }}
      />
    </div>
  );
};

export default DocumentReviewPage;
